import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { enumService } from "@/common/services/enum";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import {
  DamageCostEvaluationDto,
  DamageCostEvaluationGetPaginatedDto,
} from "@/core/api/generated/v1";

import ContractInline from "../Contract/ContractInline";
import { getVehicleOptionTitleForOperation } from "../Vehicle/VehicleAutocomplete";
import VehicleInline from "../Vehicle/VehicleInline";

export type DamageCostEvaluationAutocompleteOption =
  BaseAutocompleteOption<DamageCostEvaluationDto>;

export const getDamageCostEvaluationTitle = (data: DamageCostEvaluationDto): string =>
  StringHelper.join(
    [
      data.vehicle ? getVehicleOptionTitleForOperation(data.vehicle) : data.localNumber,
      data.localNumber,
      data.externalNumber,
      data?.stageHistory &&
        enumService.getEnumValueName("DamageCostEvaluationStage", data?.stageHistory?.stage),
    ],
    " | ",
    {
      skipEmpty: true,
      uniqueOnly: true,
    },
  );

export const damageCostEvaluationToOption = (
  data: DamageCostEvaluationDto,
): DamageCostEvaluationAutocompleteOption => ({
  id: data.id!,
  title: getDamageCostEvaluationTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface DamageCostEvaluationAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<DamageCostEvaluationDto> {
  searchFilters?: Partial<Omit<DamageCostEvaluationGetPaginatedDto, "search" | "includeIds">>;
}

export default function DamageCostEvaluationAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: DamageCostEvaluationAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={damageCostEvaluationToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.damageCostEvaluationsApi.apiV1DamageCostEvaluationsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          damageCostEvaluationGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          damageCostEvaluationGetPaginatedDto: {
            ...params.damageCostEvaluationGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Damage cost evaluation'
      placeholder='Search...'
      renderOption={(props, option: DamageCostEvaluationAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='damageCostEvaluation' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option?.data?.vehicle && (
                        <VehicleInline entity={option.data.vehicle} withIcon={false} />
                      )}
                      {option?.data?.contract && (
                        <ContractInline entity={option.data.contract} withIcon={false} />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
